import { QuestionId } from './drawer/FormRenderer/const';
import {
  ConfigurationFormData,
  CreateConfigPayload,
  ParsedRange,
  RangeInput,
  ValidationResult
} from './types';
import { AllocationSection } from './types';

export const validateDPDRanges = (ranges: RangeInput[]): ValidationResult => {
  const errors = Array(ranges.length).fill(false);

  const parsedRanges = ranges.map((r, idx) => parseRange(r, idx));

  parsedRanges.forEach(p => {
    if (!p.isValid) errors[p.originalIndex] = true;
  });

  const validRanges = parsedRanges
    .filter(p => p.isValid)
    .sort((a, b) => {
      if (a.from === b.from) return a.to - b.to;
      return a.from - b.from;
    });

  const hasOverlap = detectOverlaps(validRanges, errors);

  const anyErrors = errors.some(Boolean);

  let errorMessage = '';
  if (anyErrors) {
    if (hasOverlap && parsedRanges.some(p => !p.isValid)) {
      errorMessage =
        'Some ranges are invalid (format/bounds) and some ranges overlap. Fix both issues.';
    } else if (hasOverlap) {
      errorMessage = 'Please enter ranges that do not overlap.';
    } else {
      errorMessage = 'Please enter valid ranges';
    }
  }

  return {
    isValid: !anyErrors,
    errors,
    errorMessage
  };
};

const parseRange = (range: RangeInput, index: number): ParsedRange => {
  const rawFrom = (range.from ?? '').toString().trim();
  const rawTo = (range.to ?? '').toString().trim();

  if (rawFrom === '' && rawTo === '') {
    return { from: NaN, to: NaN, originalIndex: index, isValid: true };
  }

  if (rawFrom === '' || rawTo === '') {
    return { from: NaN, to: NaN, originalIndex: index, isValid: true };
  }

  const from = Number(rawFrom);
  const to = Number(rawTo);

  const isFiniteNumber = Number.isFinite(from) && Number.isFinite(to);
  const isInteger = Number.isInteger(from) && Number.isInteger(to);
  const inBounds = from >= 1 && to <= 180;
  const orderingOk = from <= to;

  const isValid = isFiniteNumber && isInteger && inBounds && orderingOk;

  return { from, to, originalIndex: index, isValid };
};

const detectOverlaps = (sortedRanges: ParsedRange[], errors: boolean[]): boolean => {
  let hasOverlap = false;

  for (let i = 1; i < sortedRanges.length; i++) {
    const prev = sortedRanges[i - 1];
    const curr = sortedRanges[i];

    if (curr.from <= prev.to) {
      errors[curr.originalIndex] = true;
      errors[prev.originalIndex] = true;
      hasOverlap = true;
    }
  }

  return hasOverlap;
};

export const parseDateString = (dateString: string): number[] => {
  return dateString
    .split(',')
    .map(d => parseInt(d.trim(), 10))
    .filter(d => !isNaN(d) && d >= 1 && d <= 31);
};

export const mapFormDataToPayload = (formData: ConfigurationFormData): CreateConfigPayload => {
  const parseAllocationDates = (dates: string | string[] | undefined): number[] => {
    if (!dates) return [];
    const dateStr = Array.isArray(dates) ? dates.join(', ') : (dates as string);
    return parseDateString(dateStr);
  };

  const parseDpdExclusions = (exclusions: string | undefined): string[] => {
    if (!exclusions) return [];
    return exclusions
      .split(',')
      .map(e => e.trim())
      .filter(e => e.length > 0);
  };

  return {
    usecaseId: 'CIU_ALLOCATIONS',
    version: 1,
    answers: {
      ALLOCATION_TEAM: formData.allocationTeam,
      ALLOCATION_PURPOSE: formData.allocationPurpose,
      DPD_BUCKET: formData.caseDpdBucket,
      CASE_TEAM: formData.caseTeam,
      CASE_EXPIRY_DAYS: formData.caseExpiry ? Number(formData.caseExpiry) : undefined,
      ALLOCATION_DATES: parseAllocationDates(formData.allocationDates),
      DPD_EXCLUSIONS: parseDpdExclusions(formData.dpdExclusion),
      EMI_OVERDUE_GT: formData.emiOverdue ? Number(formData.emiOverdue) : undefined,
      EMI_AMOUNT_GT: formData.emiAmount ? Number(formData.emiAmount) : undefined,
      LM_NON_ENACH_AMOUNT_GT: formData.lastMonthCollection
        ? Number(formData.lastMonthCollection)
        : undefined,
      TM_NON_ENACH_AMOUNT_GT: formData.currentMonthCollection
        ? Number(formData.currentMonthCollection)
        : undefined,
      POS_GT: formData.posAmount ? Number(formData.posAmount) : undefined,
      TM_COSMOS_NC_FEEDBACK: Array.isArray(formData.currentMonthCosmosNcFeedback)
        ? formData.currentMonthCosmosNcFeedback
        : formData.currentMonthCosmosNcFeedback
        ? [formData.currentMonthCosmosNcFeedback]
        : [],
      TM_COSMOS_NC_FEEDBACK_DAYS: formData.currentMonthCosmosNcFeedbackDays
        ? Number(formData.currentMonthCosmosNcFeedbackDays)
        : undefined,
      TM_LONGHORN_NC_FEEDBACK: Array.isArray(formData.currentMonthLonghornNcFeedback)
        ? formData.currentMonthLonghornNcFeedback
        : formData.currentMonthLonghornNcFeedback
        ? [formData.currentMonthLonghornNcFeedback]
        : [],
      TM_LONGHORN_NC_FEEDBACK_DAYS: formData.currentMonthLonghornNcFeedbackDays
        ? Number(formData.currentMonthLonghornNcFeedbackDays)
        : undefined,
      LM_COSMOS_NC_FEEDBACK: Array.isArray(formData.lastMonthCosmosNcFeedback)
        ? formData.lastMonthCosmosNcFeedback
        : formData.lastMonthCosmosNcFeedback
        ? [formData.lastMonthCosmosNcFeedback]
        : [],
      LM_LONGHORN_NC_FEEDBACK: Array.isArray(formData.lastMonthLonghornNcFeedback)
        ? formData.lastMonthLonghornNcFeedback
        : formData.lastMonthLonghornNcFeedback
        ? [formData.lastMonthLonghornNcFeedback]
        : []
    }
  };
};

const getFirstOrValue = (value: any) => (Array.isArray(value) ? value[0] : value);

const joinOrValue = (value: any) => (Array.isArray(value) ? value.join(', ') : value);

const QUESTION_ID_TO_FORM_FIELD: Record<
  string,
  { field: keyof ConfigurationFormData; transform?: (v: any) => any }
> = {
  [QuestionId.ALLOCATION_TEAM]: { field: 'allocationTeam' },
  [QuestionId.ALLOCATION_PURPOSE]: { field: 'allocationPurpose' },
  [QuestionId.DPD_BUCKET]: { field: 'caseDpdBucket' },
  [QuestionId.CASE_TEAM]: { field: 'caseTeam' },
  [QuestionId.CASE_EXPIRY_DAYS]: { field: 'caseExpiry' },
  [QuestionId.ALLOCATION_DATES]: { field: 'allocationDates', transform: joinOrValue },
  [QuestionId.DPD_EXCLUSIONS]: { field: 'dpdExclusion', transform: joinOrValue },
  [QuestionId.EMI_OVERDUE_GT]: { field: 'emiOverdue' },
  [QuestionId.EMI_AMOUNT_GT]: { field: 'emiAmount' },
  [QuestionId.LM_NON_ENACH_AMOUNT_GT]: { field: 'lastMonthCollection' },
  [QuestionId.TM_NON_ENACH_AMOUNT_GT]: { field: 'currentMonthCollection' },
  [QuestionId.POS_GT]: { field: 'posAmount' },
  [QuestionId.TM_COSMOS_NC_FEEDBACK]: { field: 'currentMonthCosmosNcFeedback' },
  [QuestionId.TM_COSMOS_NC_FEEDBACK_DAYS]: { field: 'currentMonthCosmosNcFeedbackDays' },
  [QuestionId.TM_LONGHORN_NC_FEEDBACK]: { field: 'currentMonthLonghornNcFeedback' },
  [QuestionId.TM_LONGHORN_NC_FEEDBACK_DAYS]: { field: 'currentMonthLonghornNcFeedbackDays' },
  [QuestionId.LM_COSMOS_NC_FEEDBACK]: { field: 'lastMonthCosmosNcFeedback' },
  [QuestionId.LM_LONGHORN_NC_FEEDBACK]: { field: 'lastMonthLonghornNcFeedback' }
};

export const mapApiResponseToFormData = (sections: AllocationSection[]): ConfigurationFormData => {
  const formData: ConfigurationFormData = {
    allocationTeam: '',
    allocationPurpose: '',
    caseDpdBucket: '',
    caseTeam: '',
    caseExpiry: '',
    allocationDates: '',
    dpdExclusion: ''
  };

  sections.forEach(section => {
    section.questions.forEach((question: any) => {
      const { id, answer } = question;
      const mapping = QUESTION_ID_TO_FORM_FIELD[id];

      if (mapping) {
        const value = mapping.transform ? mapping.transform(answer) : answer;
        formData[mapping.field] = value;
      }
    });
  });

  return formData;
};
